import React, { useState, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { differenceInDays } from "date-fns";
import { useFlags } from "launchdarkly-react-client-sdk";

import { Divider } from "@mui/material";

import DateRangeSelect from "components/common/DateRangeSelect";
import Daterange from "components/common/Daterange";

import { getLeaveCountV2 } from "services/useTimeoffService";
import { leaveTypeIds } from "../Enum";

import { isWeekend } from "util/utils";
import {
  DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST,
  getEndOfCurrentCalendarYear,
  getStartOfCurrentCalendarYear,
  getHolidayName,
  getSwapDateWindow,
  isValidSwapDate,
  validateLeaveDatesAgainstSwapWindow,
  validateSwapDateRange,
} from "util/swapLeave";

import "./SwappedLeavesDateSelection.scss";

function SwappedLeavesDateSelection({
  onChange,
  leavesCalendar,
  validDates,
  holidaysCalendar,
  swapDateFromAllowedInPast,
}) {
  const { swappedLeavesRevamp } = useFlags();

  const [swapDaysRange, setSwapDaysRange] = useState(null);
  const [timeOffDaysRange, setTimeOffDaysRange] = useState(null);
  const [swapDaysError, setSwapDaysError] = useState("");
  const [timeOffDaysError, setTimeOffDaysError] = useState("");
  const [swapDaysInfo, setSwapDaysInfo] = useState("");
  const [swapDaysCount, setSwapDaysCount] = useState(0);
  const [apiSwapDaysCount, setApiSwapDaysCount] = useState(null);
  const [apiErrorMessage, setApiErrorMessage] = useState("");

  const allHolidays = useSelector((state) => state?.Holidays?.allHolidays);

  const effectiveSwapDateFromAllowedInPast = swapDateFromAllowedInPast ?? DEFAULT_SWAP_DATE_FROM_ALLOWED_IN_PAST;

  // The time-off (leave) calendar's bounds are anchored to the selected swap day
  // (swapDateFrom, the holiday actually worked) — the holiday is the anchor, never the
  // other way around. No window is known (and none applies) until a swap day is picked.
  const swapDateWindow = useMemo(() => {
    if (!swapDaysRange?.startDate) return null;
    return getSwapDateWindow(swapDaysRange.startDate, effectiveSwapDateFromAllowedInPast);
  }, [swapDaysRange?.startDate, effectiveSwapDateFromAllowedInPast]);

  // The swap-days (holiday) calendar's own lower bound (PRD-2704). It must be passed
  // explicitly: Daterange falls back to `new Date(validDates.startDate)` — the employee's
  // leave-cycle start — whenever no minDate prop is given, which made a holiday worked
  // before the current cycle unreachable the moment an anniversary rolled over.
  // 1 Jan of the current year is the correct floor, and mirrors swapDaysMaxDate below:
  // the server's window upper bound is 31 Dec of year(swapDateFrom), so a holiday from an
  // earlier year can never yield a leave date in the current one.
  const swapDaysMinDate = useMemo(() => getStartOfCurrentCalendarYear(), []);

  // The swap-days (holiday) calendar's own upper bound: it must never offer a date past 31 Dec
  // of the CURRENT calendar year (PRD-1879 QA follow-up), and stays capped further by
  // validDates.endDate when that cycle-end bound is the tighter (earlier) of the two.
  const swapDaysMaxDate = useMemo(() => {
    const endOfCurrentYear = getEndOfCurrentCalendarYear();
    const validEndDate = validDates?.endDate ? new Date(validDates.endDate) : null;

    return validEndDate && validEndDate.getTime() < endOfCurrentYear.getTime() ? validEndDate : endOfCurrentYear;
  }, [validDates?.endDate]);

  // minDate = max(window.lower, validDates.startDate) — the backend also enforces
  // dateFrom >= employeeLeave.startDate for swapped types, so offering earlier dates
  // would produce a server rejection the user can't explain.
  // maxDate = window.upper, deliberately NOT intersected with validDates.endDate: swapped
  // types are exempt from the anniversary-based cycle-end bound server-side, so the leave
  // must remain selectable through 31 Dec even if the employee's cycle ends earlier. It IS,
  // however, capped at 31 Dec of the CURRENT calendar year (PRD-1879 QA follow-up): the
  // backend rejects any swapped leave beyond the current year regardless of cycle end, so
  // offering later dates only produces a save-time error the user can't explain.
  const timeOffDaysBounds = useMemo(() => {
    const validStartDate = validDates?.startDate ? new Date(validDates.startDate) : null;
    const validEndDate = validDates?.endDate ? new Date(validDates.endDate) : null;
    const endOfCurrentYear = getEndOfCurrentCalendarYear();

    if (!swapDateWindow) {
      const maxDate =
        validEndDate && validEndDate.getTime() < endOfCurrentYear.getTime() ? validEndDate : endOfCurrentYear;

      return {
        minDate: validStartDate || undefined,
        maxDate,
      };
    }

    const minDate =
      validStartDate && validStartDate.getTime() > swapDateWindow.lower.getTime()
        ? validStartDate
        : swapDateWindow.lower;

    const maxDate =
      swapDateWindow.upper.getTime() < endOfCurrentYear.getTime() ? swapDateWindow.upper : endOfCurrentYear;

    return { minDate, maxDate };
  }, [swapDateWindow, validDates?.startDate, validDates?.endDate]);

  const getSwapDaysBreakdown = useCallback(
    (startDate, endDate) => {
      if (!startDate || !endDate) return "";

      const start = moment(startDate);
      const end = moment(endDate);
      const current = start.clone();
      const holidays = new Set();
      let weekendDays = 0;

      while (current.isSameOrBefore(end)) {
        const holidayName = getHolidayName(current.toDate(), allHolidays?.data);
        if (holidayName) {
          holidays.add(holidayName);
        } else if (isWeekend(current.toDate())) {
          weekendDays += 1;
        }
        current.add(1, "day");
      }

      const labels = [];
      if (holidays.size > 0) {
        labels.push(...Array.from(holidays));
      }
      if (weekendDays > 0) {
        labels.push(`${weekendDays} weekend day${weekendDays > 1 ? "s" : ""}`);
      }

      return labels.length > 0 ? labels.join(", ") : "";
    },
    [allHolidays]
  );

  const validateTimeOffDaysAgainstWindow = (holidayDate, timeOffSelection) =>
    validateLeaveDatesAgainstSwapWindow({
      holidayDate,
      dateFrom: timeOffSelection.startDate,
      dateTo: timeOffSelection.endDate,
      swapDateFromAllowedInPast: effectiveSwapDateFromAllowedInPast,
    });

  const handleSwapDaysChange = (selection) => {
    if (selection && selection.startDate && selection.endDate) {
      const validation = validateSwapDateRange(selection.startDate, selection.endDate, allHolidays?.data);

      if (!validation.isValid) {
        setSwapDaysError(validation.error);
        setSwapDaysRange(null);
        setSwapDaysInfo("");
        setSwapDaysCount(0);
        setTimeOffDaysRange(null);
        setTimeOffDaysError("");
        setApiSwapDaysCount(null);
        setApiErrorMessage("");
        return;
      }

      setSwapDaysError("");
      setSwapDaysRange(selection);
      const breakdown = getSwapDaysBreakdown(selection.startDate, selection.endDate);
      setSwapDaysInfo(breakdown);

      const daysCount = differenceInDays(new Date(selection.endDate), new Date(selection.startDate)) + 1;
      setSwapDaysCount(daysCount);

      if (timeOffDaysRange) {
        // The anchor just changed, so the previously-selected time-off range must be
        // re-validated against the newly-anchored window (Task 4: re-validate whenever
        // either range changes).
        const windowValidation = validateTimeOffDaysAgainstWindow(selection.startDate, timeOffDaysRange);

        if (!windowValidation.isValid) {
          setTimeOffDaysError(windowValidation.error);
          setTimeOffDaysRange(null);
          setApiSwapDaysCount(null);
          setApiErrorMessage("");
        } else {
          setTimeOffDaysError("");
          validateTimeOffDays(timeOffDaysRange, selection);
        }
      } else {
        setApiSwapDaysCount(null);
        setApiErrorMessage("");
      }
    } else {
      setSwapDaysRange(null);
      setSwapDaysError("");
      setSwapDaysInfo("");
      setSwapDaysCount(0);
      setTimeOffDaysRange(null);
      setTimeOffDaysError("");
      setApiSwapDaysCount(null);
      setApiErrorMessage("");
    }
  };

  const validateTimeOffDays = async (timeOffSelection, swapSelection = swapDaysRange) => {
    if (!timeOffSelection || !timeOffSelection.startDate || !timeOffSelection.endDate) return;
    if (!swapSelection) {
      setTimeOffDaysError("Please select swap days first");
      return;
    }

    setTimeOffDaysError("");
    setApiErrorMessage("");

    try {
      const leaveCount = await getLeaveCountV2(
        timeOffSelection.startDate,
        timeOffSelection.endDate,
        leaveTypeIds.Swapped,
        "Full Day",
        swapSelection.startDate,
        swapSelection.endDate,
        swappedLeavesRevamp
      );

      if (leaveCount?.status === 200 && leaveCount?.payload?.success) {
        const availableLeaves = leaveCount.payload.leaves || 0;
        setApiSwapDaysCount(availableLeaves);
        const requestedDays =
          differenceInDays(new Date(timeOffSelection.endDate), new Date(timeOffSelection.startDate)) + 1;

        if (requestedDays > availableLeaves) {
          setTimeOffDaysError(
            `You can only select up to ${availableLeaves} time-off day${availableLeaves !== 1 ? "s" : ""} based on your selected swap days.`
          );
          setTimeOffDaysRange(null);
        } else {
          setTimeOffDaysError("");
          setApiErrorMessage("");
        }
      } else {
        setApiErrorMessage(leaveCount?.payload?.message || "Error validating time-off days");
        setTimeOffDaysError("");
      }
    } catch (error) {
      setApiErrorMessage("Error validating time-off days. Please try again.");
    }
  };

  const handleTimeOffDaysChange = async (selection) => {
    if (selection && selection.startDate && selection.endDate) {
      if (!swapDaysRange?.startDate) {
        setTimeOffDaysError("Please select swap days first");
        return;
      }

      const windowValidation = validateTimeOffDaysAgainstWindow(swapDaysRange.startDate, selection);

      if (!windowValidation.isValid) {
        setTimeOffDaysError(windowValidation.error);
        setTimeOffDaysRange(null);
        setApiSwapDaysCount(null);
        setApiErrorMessage("");
        return;
      }

      setTimeOffDaysRange(selection);
      await validateTimeOffDays(selection);
    }
  };

  const formatDateRange = (startDate, endDate, format = "MMM DD, YYYY") => {
    const start = moment(startDate);
    const end = moment(endDate);

    if (start.isSame(end, "day")) {
      return start.format(format);
    }

    return `${start.format(format)} - ${end.format(format)}`;
  };

  useEffect(() => {
    if (swapDaysRange && timeOffDaysRange && onChange) {
      onChange({
        swapDays: swapDaysRange,
        timeOffDays: timeOffDaysRange,
        swapDaysInfo,
        swapDaysBreakdown: getSwapDaysBreakdown(swapDaysRange.startDate, swapDaysRange.endDate),
        isValid: !swapDaysError && !timeOffDaysError && !apiErrorMessage,
      });
    } else if (onChange) {
      onChange({
        swapDays: swapDaysRange,
        timeOffDays: timeOffDaysRange,
        swapDaysInfo,
        swapDaysBreakdown: swapDaysRange ? getSwapDaysBreakdown(swapDaysRange.startDate, swapDaysRange.endDate) : "",
        isValid: false,
      });
    }
  }, [swapDaysRange, timeOffDaysRange, swapDaysInfo, swapDaysError, timeOffDaysError, apiErrorMessage]);

  return (
    <div className="swapped-leaves-date-selection">
      <Divider light />

      <div className="date-selection-container">
        <div className="date-selection-row">
          <div className="date-select-field">
            <DateRangeSelect
              name="swapDays"
              label="Select holiday(s) or weekend(s)"
              placeholder="Select"
              value={swapDaysRange}
              onChange={handleSwapDaysChange}
              error={!!swapDaysError}
              helperText={swapDaysError}
              dateRangeComponent={
                <Daterange
                  months={2}
                  leavesCalender={leavesCalendar}
                  holidaysCalender={holidaysCalendar}
                  isSwappedLeaves
                  validDates={validDates}
                  minDate={swapDaysMinDate}
                  maxDate={swapDaysMaxDate}
                  disabledDate={(date) => !isValidSwapDate(date, allHolidays?.data)}
                  initialDateRange={swapDaysRange}
                />
              }
            />
            {apiSwapDaysCount !== null && (
              <p className="para-body-s-regular swap-days-count">
                {apiSwapDaysCount} day{apiSwapDaysCount !== 1 ? "s" : ""} to swap
              </p>
            )}
          </div>

          <div className="date-select-field">
            <DateRangeSelect
              name="timeOffDays"
              label="Select time-off day(s)"
              placeholder="Select"
              value={timeOffDaysRange}
              onChange={handleTimeOffDaysChange}
              error={!!timeOffDaysError}
              helperText={timeOffDaysError}
              disabled={!swapDaysRange || swapDaysCount === 0}
              dateRangeComponent={
                <Daterange
                  months={2}
                  leavesCalender={leavesCalendar}
                  holidaysCalender={holidaysCalendar}
                  isSwappedLeaves={false}
                  minDate={timeOffDaysBounds.minDate}
                  maxDate={timeOffDaysBounds.maxDate}
                  disableHolidays
                  initialDateRange={timeOffDaysRange}
                />
              }
            />
          </div>
        </div>
      </div>

      {/* Time-off selected section */}
      {timeOffDaysRange && apiSwapDaysCount !== null && (
        <>
          <Divider light className="mt-1" />
          <div className="time-off-selected-section">
            <p className="para-body-s-regular time-off-label">Time-off selected</p>
            <div className="time-off-content">
              <p className="para-body-m-medium time-off-title">
                {apiSwapDaysCount} Swapped leave{apiSwapDaysCount !== 1 ? "s" : ""}
              </p>
              <p className="heading-h5-semibold time-off-dates">
                {formatDateRange(timeOffDaysRange.startDate, timeOffDaysRange.endDate)}
              </p>
              <p className="para-body-s-regular swap-info">
                Swap with: {formatDateRange(swapDaysRange.startDate, swapDaysRange.endDate)}
                {swapDaysInfo && ` (${swapDaysInfo})`}
              </p>
            </div>
          </div>
        </>
      )}

      {apiErrorMessage && (
        <div className="api-error-message">
          <p className="para-body-s-regular error-text">{apiErrorMessage}</p>
        </div>
      )}
    </div>
  );
}

export default SwappedLeavesDateSelection;
